import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, List, ListItemButton, Stack, TextField, Typography,
} from '@mui/material';
import { BusinessRounded, CheckCircleRounded, SearchRounded, WarningAmberRounded } from '@mui/icons-material';
import { buscarClientes, fetchCliente } from '../api/restaurantApi';

const GENERIC_RUT = '66666666-6';

function clean(value) { return String(value || '').trim(); }
function rut(value) { return clean(value).replace(/\./g, '').toUpperCase(); }

function getAddress(cliente = {}) {
  if (cliente.direccion_facturacion && typeof cliente.direccion_facturacion === 'object') return cliente.direccion_facturacion;
  const rows = Array.isArray(cliente.direcciones) ? cliente.direcciones : [];
  return rows.find((row) => clean(row.tipo).toLowerCase() === 'facturacion') || rows.find((row) => clean(row.tipo).toLowerCase() === 'principal') || rows[0] || null;
}

export function validateInvoiceCustomer(cliente) {
  const missing = [];
  if (!cliente) return { valid: false, missing: ['cliente'] };
  const r = rut(cliente.rut);
  const address = getAddress(cliente);
  const addressText = clean(address?.direccion_formateada) || [clean(address?.calle), clean(address?.numero)].filter(Boolean).join(' ');
  const comuna = clean(address?.comuna || cliente.comuna);
  if (!r || r === GENERIC_RUT || !r.includes('-')) missing.push('RUT tributario');
  if (!clean(cliente.giro)) missing.push('giro');
  if (!addressText) missing.push('dirección');
  if (!comuna) missing.push('comuna');
  if (cliente.activo === false) missing.push('cliente activo');
  return { valid: missing.length === 0, missing, address, addressText, comuna };
}

function name(cliente) {
  return clean(cliente?.razon_social) || [clean(cliente?.nombres), clean(cliente?.apellidos)].filter(Boolean).join(' ') || clean(cliente?.rut) || 'Cliente';
}

export default function ClienteFacturaModal({ open, onClose, onSelect }) {
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const data = await buscarClientes(query);
        setRows(data.filter((row) => rut(row?.rut) !== GENERIC_RUT));
      } catch (err) {
        setError(err?.response?.data?.message || err?.response?.data?.error || 'No se pudieron consultar los clientes.');
      } finally {
        setLoading(false);
      }
    }, 260);
    return () => window.clearTimeout(timer);
  }, [open, query]);

  useEffect(() => {
    if (open) { setQuery(''); setRows([]); setError(''); }
  }, [open]);

  const validCount = useMemo(() => rows.filter((row) => validateInvoiceCustomer(row).valid).length, [rows]);

  const select = async (row) => {
    if (selecting) return;
    setSelecting(row.id);
    setError('');
    try {
      const full = await fetchCliente(row.id);
      const validation = validateInvoiceCustomer(full);
      if (!validation.valid) {
        setError(`Cliente incompleto: ${validation.missing.join(', ')}.`);
        return;
      }
      onSelect?.(full);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || 'No se pudo cargar el cliente.');
    } finally {
      setSelecting(null);
    }
  };

  return (
    <Dialog open={open} onClose={selecting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center"><BusinessRounded color="primary" /><Box><Typography fontWeight={950}>Cliente para Factura</Typography><Typography variant="body2" color="text.secondary">RUT, razón social, nombre, email o teléfono</Typography></Box></Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: '#f7f9fc' }}>
        <TextField fullWidth autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar cliente…" InputProps={{ startAdornment: <InputAdornment position="start"><SearchRounded /></InputAdornment>, endAdornment: loading ? <CircularProgress size={18} /> : null }} sx={{ bgcolor: '#fff', mb: 1 }} />
        {error ? <Alert severity="warning" sx={{ mb: 1 }}>{error}</Alert> : null}
        <Typography variant="caption" color="text.secondary">{validCount} cliente(s) listos para Factura</Typography>
        <List sx={{ mt: 0.5, bgcolor: '#fff', border: '1px solid #dde5ee', borderRadius: 1.5, maxHeight: 360, overflowY: 'auto' }}>
          {rows.map((row) => {
            const validation = validateInvoiceCustomer(row);
            return (
              <ListItemButton key={row.id} disabled={selecting != null} onDoubleClick={() => validation.valid && select(row)}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={0.6} alignItems="center">
                    <Typography variant="body2" fontWeight={950} noWrap>{name(row)}</Typography>
                    {validation.valid ? <CheckCircleRounded sx={{ fontSize: 17, color: '#16a34a' }} /> : <WarningAmberRounded sx={{ fontSize: 17, color: '#d97706' }} />}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">{row.rut || 'Sin RUT'} · {row.giro || 'Sin giro'}</Typography>
                  {!validation.valid ? <Typography variant="caption" color="warning.dark" sx={{ display: 'block' }}>Falta: {validation.missing.join(', ')}</Typography> : null}
                </Box>
                <Button size="small" variant="contained" disabled={!validation.valid || selecting != null} onClick={(e) => { e.stopPropagation(); select(row); }}>
                  {Number(selecting) === Number(row.id) ? <CircularProgress size={17} color="inherit" /> : 'Elegir'}
                </Button>
              </ListItemButton>
            );
          })}
        </List>
      </DialogContent>
      <DialogActions><Button onClick={onClose} disabled={selecting != null}>Cancelar</Button></DialogActions>
    </Dialog>
  );
}
